import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getPriceChangeRecent,
  getPriceChangeStats,
  runPriceChangeProcessNow,
} from "../../api/adminApi";
import { Section, StatCard, ShortId, fmtDate } from "./AdminUI";

/**
 * Batch 5b-1 Section 4 — Price Activity card for the Quality Dashboard.
 *
 * Surfaces the 15-min Price Change Worker:
 *   • # drops / # raises / avg delta % over the last 24 h
 *   • Top 5 biggest drops (preview) sortet by absolute drop_pct
 *   • Per-vertical breakdown chips (auto, real_estate)
 *   • Per-source breakdown chips (willhaben_at, njuskalo_hr, …)
 *   • Manual "Process now" trigger (uses /process-now admin endpoint)
 *
 * Foundation milestone (5b-1): we only render aggregated data; the
 * actual notification send subsystem lands in 5b-2 with watchlists +
 * Apple StoreKit push.
 *
 * Polling: 60 s normally; bumps to 5 s while a manual run is enqueued.
 */
export default function PriceActivityCard() {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);

  // last-24h stats — narrow window so the card matches the "last 24h"
  // label exactly.
  const since24h = useMemo(() => {
    const d = new Date(Date.now() - 24 * 3600 * 1000);
    return d.toISOString();
  }, []);

  const statsQ = useQuery({
    queryKey: ["admin", "price-changes", "stats", since24h],
    queryFn: () => getPriceChangeStats({ since: since24h }),
    refetchInterval: running ? 5_000 : 60_000,
    refetchOnWindowFocus: !running,
  });

  const recentQ = useQuery({
    queryKey: ["admin", "price-changes", "recent", since24h],
    queryFn: () => getPriceChangeRecent({ limit: 50, since: since24h }),
    refetchInterval: running ? 5_000 : 60_000,
    refetchOnWindowFocus: !running,
  });

  const runMut = useMutation({
    mutationFn: runPriceChangeProcessNow,
    onSuccess: (data) => {
      setRunning(true);
      toast.info(
        `Price-change worker enqueued at ${data?.enqueued_at?.slice(11, 19) || "?"}`,
        { id: "pc-queued" },
      );
      // Auto-stop fast-poll after 30 s — the worker rarely takes longer.
      window.setTimeout(() => {
        setRunning(false);
        qc.invalidateQueries({ queryKey: ["admin", "price-changes"] });
      }, 30_000);
    },
    onError: (e) => {
      toast.error(`Process-Now fehlgeschlagen: ${e?.message || e}`);
    },
  });

  const stats = statsQ.data;
  const recent = useMemo(() => recentQ.data?.items ?? [], [recentQ.data]);

  // Top 5 biggest drops — sorted by absolute drop_pct in descending order.
  const top5Drops = useMemo(() => {
    return recent
      .filter((e) => {
        const op = Number(e.old_price);
        const np = Number(e.new_price);
        return op > 0 && np > 0 && np < op;
      })
      .map((e) => {
        const op = Number(e.old_price);
        const np = Number(e.new_price);
        return {
          ...e,
          drop_pct: (op - np) / op,
          drop_eur: op - np,
        };
      })
      .sort((a, b) => b.drop_pct - a.drop_pct)
      .slice(0, 5);
  }, [recent]);

  const isLoading = statsQ.isLoading || recentQ.isLoading;
  const isError = statsQ.isError || recentQ.isError;

  return (
    <Section
      title="Price Activity (last 24 h)"
      testid="price-activity-section"
      action={
        <button
          type="button"
          data-testid="price-activity-run-now-btn"
          disabled={runMut.isPending}
          onClick={() => runMut.mutate()}
          className="rounded-md border border-zinc-700 bg-zinc-900/60
                     px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800
                     disabled:cursor-not-allowed disabled:opacity-50"
        >
          {runMut.isPending ? "Queueing…" : "Process now"}
        </button>
      }
    >
      <div className="text-xs text-zinc-500 mb-3">
        Aggregated price drops + raises across all sources
      </div>
      {isLoading && (
        <div
          data-testid="price-activity-loading"
          className="text-sm text-zinc-500 py-2"
        >
          Lade Preisaktivität…
        </div>
      )}
      {isError && (
        <div
          data-testid="price-activity-error"
          className="text-sm text-rose-300 py-2"
        >
          Konnte Preisaktivität nicht laden: {String(
            statsQ.error?.message || recentQ.error?.message || "unbekannt",
          )}
        </div>
      )}

      {stats && (
        <div
          data-testid="price-activity-stats"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <StatCard
            label="Drops"
            value={stats.drops?.toLocaleString("de-AT") ?? "0"}
            testid="price-activity-stat-drops"
          />
          <StatCard
            label="Raises"
            value={stats.raises?.toLocaleString("de-AT") ?? "0"}
            testid="price-activity-stat-raises"
          />
          <StatCard
            label="Avg delta"
            value={
              stats.avg_delta_pct == null
                ? "—"
                : `${(stats.avg_delta_pct * 100).toFixed(1)}%`
            }
            testid="price-activity-stat-avg-delta"
          />
          <StatCard
            label="Pending"
            value={stats.pending_processing?.toLocaleString("de-AT") ?? "0"}
            testid="price-activity-stat-pending"
          />
        </div>
      )}

      {stats && (Object.keys(stats.per_vertical || {}).length > 0
                  || Object.keys(stats.per_source || {}).length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2"
             data-testid="price-activity-breakdown">
          {Object.entries(stats.per_vertical || {}).map(([v, n]) => (
            <span
              key={`v-${v}`}
              data-testid={`price-activity-vertical-${v}`}
              className="inline-flex items-center gap-1 rounded-md
                         border border-zinc-700 bg-zinc-900/60
                         px-2 py-1 text-xs text-zinc-300"
            >
              <span className="text-zinc-500">vertical</span>
              <span className="font-medium">{v}</span>
              <span className="text-zinc-500">{n}</span>
            </span>
          ))}
          {Object.entries(stats.per_source || {}).map(([s, n]) => (
            <span
              key={`s-${s}`}
              data-testid={`price-activity-source-${s}`}
              className="inline-flex items-center gap-1 rounded-md
                         border border-zinc-700 bg-zinc-900/60
                         px-2 py-1 text-xs text-zinc-300"
            >
              <span className="text-zinc-500">source</span>
              <span className="font-medium">{s}</span>
              <span className="text-zinc-500">{n}</span>
            </span>
          ))}
        </div>
      )}

      {top5Drops.length > 0 && (
        <div className="mt-5">
          <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Top 5 biggest drops
          </h4>
          <ul
            className="divide-y divide-zinc-800 rounded-md
                       border border-zinc-800 bg-zinc-900/40"
            data-testid="price-activity-top-drops"
          >
            {top5Drops.map((e) => (
              <li
                key={e.event_ulid}
                data-testid={`price-activity-top-${e.listing_ulid}`}
                className="flex items-center justify-between px-3 py-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ShortId value={e.listing_ulid} />
                  <span className="text-xs text-zinc-500 truncate">
                    {e.vertical} · {e.source_platform}
                  </span>
                </div>
                <div className="flex items-baseline gap-3 shrink-0">
                  <span className="text-xs text-zinc-500 line-through">
                    {Number(e.old_price).toLocaleString("de-AT")} {e.currency}
                  </span>
                  <span className="text-sm font-medium text-zinc-200">
                    {Number(e.new_price).toLocaleString("de-AT")} {e.currency}
                  </span>
                  <span
                    className="text-xs font-semibold text-rose-300"
                    data-testid={`price-activity-top-pct-${e.listing_ulid}`}
                  >
                    −{(e.drop_pct * 100).toFixed(1)}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && stats?.total_events === 0 && (
        <div
          data-testid="price-activity-empty"
          className="text-sm text-zinc-500 py-2"
        >
          Keine Preisänderungen in den letzten 24 h erfasst.
        </div>
      )}

      <div className="mt-3 text-[11px] text-zinc-600"
           data-testid="price-activity-meta">
        Window: last 24 h · polled every {running ? "5" : "60"} s ·
        {recent.length > 0 && (
          <> last event: <span title={fmtDate(recent[0].created_at)}>
            {fmtDate(recent[0].created_at)}
          </span></>
        )}
      </div>
    </Section>
  );
}
